import { Shield, FileCheck, Headphones } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const benefits = [
  {
    icon: Shield,
    title: "Soluciones a medida",
    subtitle: "(sin paquetes genéricos)",
    description: "Entendemos tu operación y diseñamos lo necesario para cubrir puntos críticos sin gastar de más."
  },
  {
    icon: FileCheck,
    title: "Implementación sin sorpresas",
    subtitle: null,
    description: "Alcance claro desde el inicio, planificación y entrega ordenada: lo acordado es lo que se instala."
  },
  {
    icon: Headphones,
    title: "Soporte real y mantenimiento",
    subtitle: null,
    description: "No es \"instalar y desaparecer\": nos aseguramos de que el sistema se mantenga estable y operativo."
  }
];

const NewValueProps = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-14 md:py-20 bg-background relative overflow-hidden transition-all duration-700",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      <div className="container px-4 sm:px-6 relative z-10">
        <div className={cn(
          "text-center mb-10 sm:mb-16 transition-all duration-700 delay-100",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        )}>
          <span className="inline-block text-primary font-medium mb-3 uppercase tracking-wide text-sm">
            Por qué elegirnos
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
            La diferencia está en cómo trabajamos
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            No vendemos productos: resolvemos problemas con ingeniería clara y acompañamiento real.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={cn(
                "group relative p-5 sm:p-8 bg-card rounded-2xl border border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-lg hover:-translate-y-1",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: isVisible ? `${200 + index * 100}ms` : "0ms" }}
            >
              {/* Accent line */}
              <div className="absolute top-0 left-8 right-8 h-1 bg-gradient-to-r from-primary to-secondary rounded-b opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <benefit.icon className="w-7 h-7 text-primary" />
              </div>

              <h3 className="text-xl font-semibold text-foreground mb-1">
                {benefit.title}
              </h3>
              {benefit.subtitle && (
                <span className="text-sm text-primary font-medium">
                  {benefit.subtitle}
                </span>
              )}
              <p className="text-muted-foreground mt-3 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewValueProps;
